// Per-segment encoded-chunk cache (Local Engine Phase 3).
// Splits the timeline into fixed segments; each segment's ENCODED video chunks are
// cached in OPFS keyed by a content hash of the captions/style active in that window.
// On re-export, unchanged segments are re-muxed straight from cache (no composite,
// no encode) and only changed segments are re-rendered. Falls back cleanly when OPFS
// is unavailable (every segment is treated as a miss).

import { opfsCache, sha256Hex } from "./opfsCache";

export const SEG_SECONDS = 2.0;

const b64 = {
  enc: (buf) => {
    const bytes = new Uint8Array(buf);
    let s = "";
    for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    return btoa(s);
  },
  dec: (str) => {
    const bin = atob(str);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  },
};

// Signature of everything that affects a segment's pixels within [t0,t1).
export async function segmentHash({ sourceKey, t0, t1, fps, width, height, codec, layers, config, segments }) {
  const win = { t0: +t0.toFixed(3), t1: +t1.toFixed(3), fps, width, height, codec, sourceKey };
  // captions active in the window (both transcript + object-layer models)
  const activeSegs = (segments || [])
    .filter((s) => (s.end ?? 0) > t0 && (s.start ?? 0) < t1)
    .map((s) => ({ s: s.start, e: s.end, w: (s.words || []).map((w) => [w.word, w.start, w.end]) }));
  const flat = [];
  (layers || []).forEach((L) => (L.objects || L.captions || []).forEach((o) => {
    if ((o.end ?? o.t1 ?? 0) > t0 && (o.start ?? o.t0 ?? 0) < t1) {
      flat.push({ t: [o.start ?? o.t0, o.end ?? o.t1], x: o.x, y: o.y, txt: o.text, an: o.animation, st: o.style, sc: o.scale, r: o.rotation });
    }
  }));
  const styleKeys = ["fontFamily", "animation", "animationTarget", "textColor", "outline", "outlineColor",
    "shadow", "glowEnabled", "glowColor", "bgEnabled", "bgColor", "bgOpacity", "bgRadius", "lineHeight", "align", "enterDur"];
  const style = {};
  styleKeys.forEach((k) => { if (config && config[k] !== undefined) style[k] = config[k]; });
  const payload = JSON.stringify({ win, activeSegs, flat, style });
  const enc = new TextEncoder().encode(payload);
  const hex = await sha256Hex(enc.buffer);
  return `seg_${hex}.json`;
}

export async function loadSegment(key) {
  const f = await opfsCache.get(key);
  if (!f) return null;
  try {
    const json = JSON.parse(await f.text());
    const chunks = json.chunks.map((c) => new window.EncodedVideoChunk({
      type: c.type, timestamp: c.timestamp, duration: c.duration, data: b64.dec(c.data),
    }));
    const description = json.description ? b64.dec(json.description) : null;
    return { codec: json.codec, description, chunks };
  } catch { return null; }
}

export async function saveSegment(key, { codec, description, chunks }) {
  const json = {
    codec,
    description: description ? b64.enc(description.buffer || description) : null,
    chunks: chunks.map((c) => ({ type: c.type, timestamp: c.timestamp, duration: c.duration, data: b64.enc(c.data) })),
  };
  try { await opfsCache.put(key, new Blob([JSON.stringify(json)], { type: "application/json" })); } catch { /* ignore */ }
}

// Serialize a live EncodedVideoChunk to a plain object with bytes (for saveSegment).
export function chunkToPlain(chunk) {
  const buf = new ArrayBuffer(chunk.byteLength);
  chunk.copyTo(buf);
  return { type: chunk.type, timestamp: chunk.timestamp, duration: chunk.duration, data: buf };
}
